/**
 * "Which of the SELECTED entities did this command DECLINE, and how many did
 * it actually transform?" — the ONE document walk behind every per-entity
 * refusal the shell reducer reports.
 *
 * ## Why this is one domain truth and not two similar loops
 *
 * A refusal reaches the user through two sites that must never disagree: the
 * geometry DOCUMENT reducer, which leaves the declined entity untouched, and
 * the SHELL reducer, which needs a COUNT and a "was anything transformed at
 * all?" to choose between the "…, N declined" row and the "nothing happened,
 * the selection was only declinable objects" row.
 *
 * That second question is identical for every refusal there will ever be:
 * resolve the dispatched ids against the document's spaces, split the ones
 * that resolved by a per-entity predicate, and count the rest. Only the
 * PREDICATE differs — `|factor| !== 1` on a `Table`
 * ({@link import("./tableScaleRefusal").isTableScaleRefused}) versus an oblique
 * affine on an `OLE Frame`
 * ({@link import("./oleFrameTransformRefusal").isOleFrameTransformRefused}) —
 * and so does the message pair each one raises. The predicate is therefore a
 * PARAMETER (a function, not a mode flag: this helper can neither name nor
 * branch on which refusal it is walking for), while each refusal keeps its own
 * predicate module, its own sources and its own message ids.
 *
 * Copying the walk instead would put the id-resolution rules — de-duplication,
 * "an id that matches nothing counts as neither", document order — in two
 * places that drift independently, which is exactly the clone
 * `npm run dup:check` gates on and CLAUDE.md ПРИОРИТЕТ #1 tells us to EXTRACT
 * rather than baseline.
 */

import type { DrawingEntity, DrawingSpace } from "@dxf-ai-editor/drawing-model";

/** What a dispatch will actually do to its target set. */
export interface SelectionRefusalSummary {
  /** Ids of the matched entities the command declines, in document order. */
  readonly refusedEntityIds: readonly string[];
  /** How many matched entities WILL be transformed — 0 means the whole selection was declined. */
  readonly transformedCount: number;
}

/**
 * Resolve `entityIds` against `spaces` and split them into "declined" and
 * "will transform", using `isRefused` as the single per-entity predicate.
 *
 * Ids that match no entity in `spaces` count as NEITHER: an unresolvable id is
 * not something the command declined, and it is not something it transformed.
 *
 * ONE document walk, O(1) per entity. A repeated id in `entityIds` is counted
 * once — the `Set` constructor already collapses it — exactly as the reducers'
 * own `Set`-keyed transforms treat it; `wanted.delete` rather than
 * `wanted.has` additionally makes a DOCUMENT that listed one id in two spaces
 * count once, which `drawingDocumentSchema` forbids, so the two forms are
 * indistinguishable on every input this package can be handed. MEASURED, not
 * assumed: `has` was substituted for `delete` on this HEAD and the eight
 * suites that reach this walk re-run — 117/117 still green. `delete` is kept
 * as the stricter of the two. Likewise NO early exit: an "all ids found, stop
 * early" short-circuit changes only how long the walk takes, never its result
 * (checked the same way — a `wanted.size === 0` break added to BOTH loops, the
 * same 117 tests still green), while buying nothing, because the reducers this
 * feeds already map every space of the document on the same dispatch
 * (`applyInPlaceTransformWithMutations`).
 *
 * Neither of those two is a Stryker MUTANT, which is what an earlier revision
 * of this comment called them ("an EQUIVALENT mutant", "three equivalent
 * mutants in the targeted mutation run"). StrykerJS's `MethodExpression`
 * mutator carries a fixed replacement table — `endsWith`/`startsWith`,
 * `every`/`some`, `filter`, `trim`, `min`/`max`, ... — with no entry for
 * `delete` or `has`, and no mutator ADDS an early exit to a loop, so the
 * targeted gate generates NEITHER. This module therefore carries no
 * equivalent-mutant suppression at all: its measured score is 13 mutants, 13
 * killed, 0 survived, 0 NoCoverage.
 */
export function summarizeSelectionRefusal(
  spaces: readonly DrawingSpace[],
  entityIds: readonly string[],
  isRefused: (entity: DrawingEntity) => boolean,
): SelectionRefusalSummary {
  const wanted = new Set(entityIds);
  const refusedEntityIds: string[] = [];
  let transformedCount = 0;
  for (const space of spaces) {
    for (const entity of space.entities) {
      if (!wanted.delete(entity.id)) continue;
      if (isRefused(entity)) {
        refusedEntityIds.push(entity.id);
      } else {
        transformedCount += 1;
      }
    }
  }
  return { refusedEntityIds, transformedCount };
}
